import { cva } from 'class-variance-authority';
import type { MouseEventHandler } from 'react';
import { Link } from 'react-router';
import { Icon } from '~/components/granular';
import { classNames } from '~/utils/styles';
import type { CardProps } from './Card.types';

const variants = cva('flex w-full items-center border border-neutral-300 py-6', {
  compoundVariants: [
    {
      appearance: 'default',
      class: 'cursor-pointer hover:border-teal-700 [&_.link-icon]:hover:text-teal-700',
      isInteractive: true,
    },
  ],
  variants: {
    appearance: {
      default: 'bg-white',
      disabled: 'bg-neutral-200 text-neutral-600',
      readonly: 'border-transparent bg-[#F2F4F6]',
    },
    isInteractive: {
      false: 'cursor-default',
    },
  },
});

const leftIconVariants = cva('mr-6 rounded-full p-3', {
  variants: {
    appearance: {
      default: 'bg-teal-200 text-teal-700',
      disabled: 'bg-neutral-300 text-neutral-800',
      readonly: 'bg-technology-anyRenewable/10 text-neutral-800',
    },
  },
});

export function Card({ appearance = 'default', className, leftIcon, children, to, onClick, ...props }: CardProps) {
  const isInteractive = !!to || !!onClick;

  const card = (
    <div className={classNames(variants({ appearance, isInteractive }), className)} {...props} data-testid="card">
      {leftIcon && (
        <span className={leftIconVariants({ appearance })}>
          <Icon icon={leftIcon} />
        </span>
      )}

      {children}

      {isInteractive && (
        <div className="ml-auto">
          <Icon className="link-icon ml-6 shrink-0 text-neutral-600" data-testid="card-icon" icon="ArrowRightIcon" />
        </div>
      )}
    </div>
  );

  if (to) {
    return (
      <Link className="contents" to={to}>
        {card}
      </Link>
    );
  }

  if (onClick) {
    return (
      <button
        className="contents text-left"
        type="button"
        onClick={onClick as unknown as MouseEventHandler<HTMLButtonElement>}
      >
        {card}
      </button>
    );
  }

  return card;
}
